import { useToastStore } from "./toast.js";
import { create } from "zustand";
import type { AttackPlan, BattleResult, Layout } from "@empire/core-sim";
import {
  RESOURCE_KINDS,
  TRIALS,
  fromLayout,
  playTrial,
  trialById,
  trialLayout,
  type Resources,
  type Trial,
} from "@empire/shared";
import { attemptTrial, fetchTrials } from "../api/progress.js";
import { rerun, type Frame } from "./replay.js";
import { useEditorStore } from "./editor.js";
import { toPlan, usePlanStore } from "./plan.js";
import { useSessionStore } from "./session.js";
import { useVillageStore } from "./village.js";

/**
 * Trials (29 Sep 2026): hand-made battles a new village can play while its
 * builders work. The battle is scored here at once with the same function the
 * server uses, so the replay starts without waiting; the server re-runs it
 * before paying the stars' rewards.
 *
 * A defend trial borrows the editor store for its board, like the tutorial
 * does, and hands the player's own defence back when it closes.
 */

export interface TrialOutcome {
  readonly stars: 0 | 1 | 2 | 3;
  readonly result: BattleResult;
  readonly layout: Layout;
  readonly plan: AttackPlan;
}

interface TrialsState {
  /** Best stars per trial id. */
  progress: Record<string, number>;
  openId: string | null;
  phase: "plan" | "result";
  outcome: TrialOutcome | null;
  frames: Frame[];
  watching: boolean;
  /** What the server paid for this attempt; null until it answers. */
  reward: Resources | null;
  saving: boolean;
  saveError: string | null;
  /** Why "Fight" did nothing. */
  message: string | null;
  /** The editor's board before a defend trial borrowed it. */
  borrowed: Layout | null;

  load: () => Promise<void>;
  open: (id: string) => void;
  close: () => void;
  fight: () => void;
  retry: () => void;
  finishWatching: () => void;
}

export const useTrialsStore = create<TrialsState>((set, get) => ({
  progress: {},
  openId: null,
  phase: "plan",
  outcome: null,
  frames: [],
  watching: false,
  reward: null,
  saving: false,
  saveError: null,
  message: null,
  borrowed: null,

  load: async () => {
    const { villageId } = useSessionStore.getState();
    const token = await useSessionStore.getState().token();
    if (!villageId || !token) return;
    try {
      const answer = await fetchTrials(villageId, token);
      const progress = { ...get().progress };
      for (const row of answer.progress)
        progress[row.trialId] = Math.max(progress[row.trialId] ?? 0, row.stars);
      set({ progress });
    } catch {
      /* the list still works; stars fill in when the server answers */
    }
  },

  open: (id) => {
    const trial = trialById(id);
    if (!trial) return;
    get().close();
    if (trial.kind === "defend") {
      const editor = useEditorStore.getState();
      set({ borrowed: editor.layout });
      editor.load(trialLayout(trial));
      editor.setBrush("road");
    } else {
      usePlanStore.getState().setArmy(trial.army);
    }
    set({
      openId: id,
      phase: "plan",
      outcome: null,
      frames: [],
      watching: false,
      reward: null,
      saveError: null,
      message: null,
    });
  },

  close: () => {
    const { borrowed, openId } = get();
    if (!openId) return;
    if (borrowed) useEditorStore.getState().load(borrowed);
    usePlanStore.getState().setArmy(null);
    set({
      openId: null,
      borrowed: null,
      phase: "plan",
      outcome: null,
      frames: [],
      watching: false,
      message: null,
    });
  },

  fight: () => {
    const trial = current();
    if (!trial) return;
    let submission: { plan: AttackPlan } | { layout: Layout };
    if (trial.kind === "attack") {
      const plan = toPlan(usePlanStore.getState().waves);
      if (plan.waves.length === 0) {
        set({ message: "Add some units to a wave first." });
        return;
      }
      submission = { plan };
    } else {
      submission = { layout: useEditorStore.getState().layout };
    }
    const score = playTrial(trial, submission);
    if (!score.ok) {
      set({ message: score.message });
      return;
    }
    const { frames } = rerun(score.layout, { plan: score.plan });
    const progress = { ...get().progress };
    progress[trial.id] = Math.max(progress[trial.id] ?? 0, score.stars);
    set({
      phase: "result",
      outcome: { stars: score.stars, result: score.result, layout: score.layout, plan: score.plan },
      frames,
      watching: frames.length > 0,
      progress,
      reward: null,
      saveError: null,
      message: null,
    });
    void save(trial, submission);
  },

  retry: () =>
    set({
      phase: "plan",
      outcome: null,
      frames: [],
      watching: false,
      reward: null,
      saveError: null,
    }),

  finishWatching: () => {
    if (get().watching) set({ watching: false });
  },
}));

function current(): Trial | undefined {
  const id = useTrialsStore.getState().openId;
  return id ? trialById(id) : undefined;
}

/** Tell the server, which re-scores the attempt and pays any new stars. */
async function save(
  trial: Trial,
  submission: { plan: AttackPlan } | { layout: Layout }
): Promise<void> {
  const { villageId } = useSessionStore.getState();
  const token = await useSessionStore.getState().token();
  if (!villageId || !token) {
    useTrialsStore.setState({ saveError: "Not signed in — stars were not saved." });
    return;
  }
  useTrialsStore.setState({ saving: true });
  try {
    const answer = await attemptTrial(
      villageId,
      token,
      trial.id,
      "plan" in submission
        ? {
            plan: {
              waves: submission.plan.waves.map((w) => ({
                offsetTicks: w.offsetTicks,
                units: [...w.units],
              })),
            },
          }
        : { layout: fromLayout(submission.layout) }
    );
    useVillageStore.getState().receive(answer.village);
    if (answer.recovered)
      useToastStore.getState().show("Your army has recovered: 10 Militia arrived.");
    const progress = { ...useTrialsStore.getState().progress, [trial.id]: answer.best };
    useTrialsStore.setState({ saving: false, reward: answer.reward, progress });
  } catch (error) {
    useTrialsStore.setState({
      saving: false,
      saveError: error instanceof Error ? error.message : "Could not reach the server.",
    });
  }
}

/** A trial is open to play once the one before it has at least one star. */
export function unlocked(progress: Record<string, number>, index: number): boolean {
  if (index === 0 || TRIALS[index]?.id === "recovery") return true;
  const before = TRIALS[index - 1];
  return !!before && (progress[before.id] ?? 0) > 0;
}

export function rewardTotal(reward: Resources | null): number {
  return reward ? RESOURCE_KINDS.reduce((n, k) => n + reward[k], 0) : 0;
}
